import type { DependencyList } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

type TDefaultFunction = (props?: unknown) => unknown;

type TError = Error | undefined;

type TUseLoading<Callback> = {
    /** Функция */
    callback: Callback;
    /** Зависимости */
    deps?: DependencyList;
};

export type TUseLoadingReturn<Callback> = [Callback, boolean, TError];

/** Следит за статусом загрузки и ошибки при выполнении функции */
export const useFunctionNotifier = <Callback extends TDefaultFunction>({
    callback,
    deps,
}: TUseLoading<Callback>): TUseLoadingReturn<Callback> => {
    const isMountedRef = useRef(true);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<TError>(undefined);

    useEffect(
        () => () => {
            isMountedRef.current = false;
        },
        []
    );

    const depsList = deps || [callback];

    const trigger = useCallback(async (...args: unknown[]) => {
        setLoading(true);

        let result;

        try {
            result = await callback(...args);
            // eslint-disable-next-line
        } catch (e: any) {
            setError(e);
        } finally {
            /* istanbul ignore next */
            if (isMountedRef.current) {
                setLoading(false);
            }
        }

        return result;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, depsList);

    return [trigger as Callback, loading, error];
};
